// ═══════════════════════════════════════════════════════════════════════
// Module 1 — Référentiel des métiers (D-E1). Gestion simple pour les RH :
//   • les métiers (plusieurs salariés partagent un métier ; les postes restent réservés aux
//     responsabilités) : créer, renommer, archiver ;
//   • les libellés saisis sur les anciennes fiches : jamais transformés automatiquement — le RH
//     choisit le métier, voit combien de salariés seront rattachés, puis confirme.
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Layers, Plus, Archive, ArchiveRestore, Link2, Pencil, X, Tags } from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { EnTetePage, Carte, Bouton, Badge, BandeauInfo, EtatVide, Squelette } from '../design/composants';

const FAMILLES = { OUVRIERS: 'Ouvriers', EMPLOYES: 'Employés', AGENTS_MAITRISE: 'Agents de maîtrise', CADRES: 'Cadres' };
const ROLES_RH = ['drh', 'rh_principal', 'assistant_rh'];

function ModaleMetier({ metier, onFermer, onTermine }) {
  const [f, setF] = useState({ libelle: metier?.libelle || '', famille_professionnelle: metier?.famille_professionnelle || '', description: metier?.description || '' });
  const [erreur, setErreur] = useState('');
  const enregistrer = async () => {
    try {
      const { data } = metier ? await axios.put(`/api/metiers/${metier.id}`, f) : await axios.post('/api/metiers', f);
      onTermine(data.message, data.metier);
    } catch (e) { setErreur(e.response?.data?.message || 'Enregistrement impossible.'); }
  };
  return (
    <div role="dialog" aria-modal="true" aria-label={metier ? 'Modifier le métier' : 'Nouveau métier'} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="rh-carte" style={{ width: 460, maxWidth: '100%', boxShadow: 'var(--sg-ombre-haute)', marginBottom: 0 }}>
        <div className="rh-carte-entete" style={{ marginBottom: 12 }}>
          <h2 className="rh-carte-titre">{metier ? <Pencil size={17} aria-hidden="true" /> : <Plus size={17} aria-hidden="true" />}{metier ? 'Modifier le métier' : 'Nouveau métier'}</h2>
          <button type="button" className="rh-btn rh-btn-discret rh-btn-icone" onClick={onFermer} aria-label="Fermer"><X size={18} /></button>
        </div>
        {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>Libellé <span style={{ color: 'var(--sg-danger)' }}>*</span></span>
          <input className="rh-champ-saisie" value={f.libelle} onChange={(e) => setF({ ...f, libelle: e.target.value })} placeholder="Ex. Abatteur, Agent administratif, Comptable" />
        </label>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>Famille professionnelle (indicative)</span>
          <select className="rh-champ-saisie" value={f.famille_professionnelle} onChange={(e) => setF({ ...f, famille_professionnelle: e.target.value })}>
            <option value="">Non précisée</option>
            {Object.entries(FAMILLES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>Description (facultatif)</span>
          <input className="rh-champ-saisie" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Bouton onClick={onFermer}>Annuler</Bouton>
          <Bouton variante="primaire" onClick={enregistrer}>Enregistrer</Bouton>
        </div>
      </div>
    </div>
  );
}

export default function MetiersPage() {
  const { employe: user } = useAuth();
  const [metiers, setMetiers] = useState(null);
  const [libelles, setLibelles] = useState([]);
  const [message, setMessage] = useState('');
  const [erreur, setErreur] = useState('');
  const [edition, setEdition] = useState(null); // {} = nouveau, objet = modification
  const [rattachement, setRattachement] = useState({}); // libellé → métier choisi
  const [aConfirmer, setAConfirmer] = useState(null); // { libelle, nb_salaries, metier }
  const peutGerer = ROLES_RH.includes(user?.role);

  const charger = async () => {
    try {
      const [m, l] = await Promise.all([axios.get('/api/metiers'), peutGerer ? axios.get('/api/metiers/libelles') : Promise.resolve({ data: { libelles: [] } })]);
      setMetiers(m.data.metiers); setLibelles(l.data.libelles);
    } catch (e) { setErreur(e.response?.data?.message || 'Chargement impossible.'); }
  };
  useEffect(() => { charger(); }, []);

  const signaler = (m) => { setMessage(m); setErreur(''); charger(); };
  const archiver = async (m, actif) => {
    try { const { data } = await axios.put(`/api/metiers/${m.id}`, { actif }); signaler(data.message); } catch (e) { setErreur(e.response?.data?.message || 'Opération impossible.'); }
  };
  // Confirmation explicite : le nombre de salariés concernés est annoncé avant l'écriture.
  const rattacher = (lib) => {
    const cible = metiers.find((x) => x.id === rattachement[lib.libelle]);
    if (cible) setAConfirmer({ ...lib, metier: cible });
  };
  const confirmerRattachement = async () => {
    const c = aConfirmer; setAConfirmer(null);
    try { const { data } = await axios.post(`/api/metiers/${c.metier.id}/rattacher`, { libelle: c.libelle }); signaler(data.message); } catch (e) { setErreur(e.response?.data?.message || 'Rattachement impossible.'); }
  };

  const actifs = (metiers || []).filter((m) => m.actif);
  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Employés', vers: '/employes' }, { libelle: 'Métiers' }]} titre="Métiers"
          sousTitre="Plusieurs salariés partagent un métier. Les postes de l'organigramme restent réservés aux responsabilités."
          actions={peutGerer && <Bouton variante="primaire" Icone={Plus} onClick={() => setEdition({})}>Nouveau métier</Bouton>} />
        {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
        {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        {aConfirmer && (
          <div role="alertdialog" aria-modal="true" aria-label="Confirmer le rattachement" style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div className="rh-carte" style={{ width: 460, maxWidth: '100%', boxShadow: 'var(--sg-ombre-haute)', marginBottom: 0 }}>
              <h2 className="rh-carte-titre" style={{ marginBottom: 10 }}><Link2 size={17} aria-hidden="true" />Rattacher au métier « {aConfirmer.metier.libelle} »</h2>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--sg-texte-2)' }}>
                {aConfirmer.nb_salaries} salarié{aConfirmer.nb_salaries > 1 ? 's' : ''} portant le libellé « {aConfirmer.libelle} » recevr{aConfirmer.nb_salaries > 1 ? 'ont' : 'a'} ce métier. Le rattachement est tracé dans l'historique de chacun et peut être changé ensuite fiche par fiche.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Bouton onClick={() => setAConfirmer(null)}>Annuler</Bouton>
                <Bouton variante="primaire" onClick={confirmerRattachement}>Confirmer le rattachement</Bouton>
              </div>
            </div>
          </div>
        )}
        {edition && <ModaleMetier metier={edition.id ? edition : null} onFermer={() => setEdition(null)} onTermine={(m) => { setEdition(null); signaler(m); }} />}

        <Carte titre="Référentiel" Icone={Layers}>
          {!metiers ? <Squelette largeur="50%" /> : metiers.length === 0 ? (
            <EtatVide Icone={Layers} titre="Aucun métier défini" texte="Créez les métiers de SOGAS, puis rattachez-y les salariés (ci-dessous pour les libellés existants)." />
          ) : (
            <ul className="rh-liste">
              {metiers.map((m) => (
                <li key={m.id} style={{ flexWrap: 'wrap', opacity: m.actif ? 1 : 0.6 }}>
                  <span style={{ flex: 1, minWidth: 200 }}>
                    <strong style={{ fontWeight: 600 }}>{m.libelle}</strong>
                    <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{m.code}{m.famille_professionnelle ? ` · ${FAMILLES[m.famille_professionnelle]}` : ''}{m.description ? ` · ${m.description}` : ''}</span>
                  </span>
                  <Badge ton="neutre">{m.nb_salaries} salarié{m.nb_salaries > 1 ? 's' : ''}</Badge>
                  {!m.actif && <Badge ton="neutre">Archivé</Badge>}
                  {peutGerer && <Bouton petit variante="discret" Icone={Pencil} onClick={() => setEdition(m)}>Modifier</Bouton>}
                  {peutGerer && (m.actif
                    ? <Bouton petit variante="discret" Icone={Archive} onClick={() => archiver(m, false)}>Archiver</Bouton>
                    : <Bouton petit variante="discret" Icone={ArchiveRestore} onClick={() => archiver(m, true)}>Réactiver</Bouton>)}
                </li>
              ))}
            </ul>
          )}
        </Carte>

        {peutGerer && (
          <Carte titre="Libellés des anciennes fiches à rattacher" Icone={Tags}>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--sg-texte-3)' }}>
              Ces libellés ont été saisis librement sur les fiches. Ils ne deviennent pas des métiers d'eux-mêmes : choisissez le métier correspondant, puis confirmez.
            </p>
            {libelles.length === 0 ? <EtatVide Icone={Tags} titre="Aucun libellé à rattacher" texte="Tous les salariés en activité ont un métier ou un poste." /> : (
              <ul className="rh-liste">
                {libelles.map((l) => (
                  <li key={l.libelle} style={{ flexWrap: 'wrap' }}>
                    <span style={{ flex: 1, minWidth: 180 }}>« {l.libelle} »<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{l.nb_salaries} salarié{l.nb_salaries > 1 ? 's' : ''}</span></span>
                    <select className="rh-champ-saisie" style={{ width: 'auto', minWidth: 200 }} value={rattachement[l.libelle] || ''} aria-label={`Métier pour ${l.libelle}`}
                      onChange={(e) => setRattachement({ ...rattachement, [l.libelle]: e.target.value })}>
                      <option value="">Choisir un métier…</option>
                      {actifs.map((m) => <option key={m.id} value={m.id}>{m.libelle}</option>)}
                    </select>
                    <Bouton petit Icone={Link2} disabled={!rattachement[l.libelle]} onClick={() => rattacher(l)}>Rattacher</Bouton>
                  </li>
                ))}
              </ul>
            )}
          </Carte>
        )}
      </div>
    </Layout>
  );
}
